import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, Switch, FormControlLabel,
    LinearProgress, Breadcrumbs, Link, Stack
    } from '@mui/material';
import { Home } from '@mui/icons-material';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import storyApi from '../../../../services/storyApi';

const StoryBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        isActive: true
    });
    const [previewUrl] = useState('');
    const [errors, setErrors] = useState({ title: '', content: '' });
    const [touched, setTouched] = useState({ title: false, content: false});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isNew) {
            const fetchById = async () => {
                try {
                    const story = await storyApi.getById(id);
                    setFormData({
                        title: story.title || '',
                        content: story.content || '',
                        isActive: story.isActive
                    });

                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load story', 'error');
                    navigate('/admin/stories');
                } finally {
                    setLoading(false);
                }
            };
            fetchById();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const validateField = (name, value) => {
        switch (name) {
            case 'title': return value.trim() ? '' : 'Title is required';
            case 'content': return value.trim() ? '' : 'content is required';
            default: return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };



    const handleSubmit = async () => {
        setTouched({ title: true, content: true });
        if (!formData.title.trim() || !formData.content.trim()) {
            setErrors({
                title: !formData.title.trim() ? 'Title is required' : '',
                content: !formData.content.trim() ? 'content is required' : ''
            });
            return;
        }


        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to ${isNew ? 'create' : 'update'} this story?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });

        if (!confirm.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('content', formData.content || '');
            formDataToSend.append('isActive', formData.isActive);


            if (isNew) {
                await storyApi.create(formDataToSend);
            } else {
                await storyApi.update(id, formDataToSend);
            }

            Swal.fire('Success', `story ${isNew ? 'created' : 'updated'}`, 'success');
            navigate('/admin/stories');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/stories')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="/admin" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Master
                            </Link>
                            <Link href="/admin/stories" underline="hover" color="inherit">
                                Story
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Story Information</Typography>
                            <TextField
                                required
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                fullWidth
                                label="Content"
                                name="content"
                                value={formData.content}
                                onChange={handleInputChange}
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        alignItems: 'flex-start'
                                    }
                                }}
                                size="small"
                                multiline
                                rows={4}
                                maxRows={20}
                            />


                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={(e) =>
                                    setFormData(prev => ({ ...prev, isActive: e.target.checked }))
                                } />}
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/stories')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StoryBody;